import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Plus, Trash2, GripVertical, Calculator, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { useCommunity } from '@/contexts/CommunityContext';
import { supabase } from '@/integrations/supabase/client';

export interface QuoteItemFormData {
  id?: string;
  label: string;
  quantity: number;
  unit_label: string;
  unit_price: number;
  line_total: number;
  display_order: number;
}

interface QuoteItemsManagerProps {
  items: QuoteItemFormData[];
  onChange: (items: QuoteItemFormData[]) => void;
  priceType: 'fixed' | 'estimate' | 'range';
  businessId?: string; // Optional business ID for category-specific unit suggestions
  quoteLimits?: {
    fixed_price?: number;
    estimated_price?: number;
    min_price?: number;
    max_price?: number;
  };
}

// Function to fetch category-specific unit suggestions
const fetchCategoryUnitSuggestions = async (businessId: string) => {
  const { data, error } = await supabase
    .from('businesses')
    .select(`
      category:categories (
        unit_suggestions
      )
    `)
    .eq('id', businessId)
    .single();

  if (error) {
    console.error('Error fetching category unit suggestions:', error);
    return null;
  }

  return data?.category?.unit_suggestions || null;
};

export const QuoteItemsManager: React.FC<QuoteItemsManagerProps> = ({
  items,
  onChange,
  priceType,
  businessId,
  quoteLimits
}) => {
  const { getParameter } = useCommunity();
  const currency = getParameter<string>('currency') || '$';

  const [localItems, setLocalItems] = useState<QuoteItemFormData[]>(items);
  const [expandedItemIndex, setExpandedItemIndex] = useState<number | null>(null);
  const [customUnitItems, setCustomUnitItems] = useState<Set<string>>(new Set());

  // Fetch category-specific unit suggestions if businessId is provided
  const { data: categoryUnitSuggestions } = useQuery({
    queryKey: ['category-unit-suggestions', businessId],
    queryFn: () => fetchCategoryUnitSuggestions(businessId!),
    enabled: !!businessId,
    staleTime: 5 * 60 * 1000 // 5 minutes cache
  });

  useEffect(() => {
    // Ensure all items have IDs and quantityInput values
    const itemsWithIds = items.map((item, index) => ({
      ...item,
      id: item.id || `existing-${index}-${Date.now()}`,
      quantityInput: (item as any).quantityInput ?? item.quantity?.toString() ?? '1'
    }));
    setLocalItems(itemsWithIds);
  }, [items]);

  const addItem = () => {
    const newItem: QuoteItemFormData = {
      id: `temp-${Date.now()}-${Math.random()}`, // Unique temporary ID
      label: '',
      quantity: 1,
      unit_label: 'item',
      unit_price: 0,
      line_total: 0,
      display_order: localItems.length,
      quantityInput: '1' // Initialize quantityInput for new items
    } as any;
    
    const updatedItems = [...localItems, newItem];
    setLocalItems(updatedItems);
    onChange(updatedItems);
    
    // Expand the newly added item and collapse others
    setExpandedItemIndex(localItems.length);
  };

  const removeItem = (index: number) => {
    const itemToRemove = localItems[index];
    const updatedItems = localItems.filter((_, i) => i !== index)
      .map((item, i) => ({ ...item, display_order: i }));
    setLocalItems(updatedItems);
    onChange(updatedItems);
    
    // Clean up custom unit tracking for removed item
    if (itemToRemove?.id) {
      const newCustomUnits = new Set(customUnitItems);
      newCustomUnits.delete(itemToRemove.id);
      setCustomUnitItems(newCustomUnits);
    }
    
    // Reset expanded item if we removed it
    if (expandedItemIndex === index) {
      setExpandedItemIndex(null);
    } else if (expandedItemIndex !== null && expandedItemIndex > index) {
      setExpandedItemIndex(expandedItemIndex - 1);
    }
  };

  const updateItem = (index: number, field: keyof QuoteItemFormData, value: string | number) => {
    const updatedItems = [...localItems];
    const item = { ...updatedItems[index] };
    
    if (field === 'quantity') {
      // For quantity, store the raw string value to allow editing, but ensure it's a valid number for calculations
      if (typeof value === 'string') {
        // Store the raw input value (including empty string)
        (item as any).quantityInput = value;
        
        if (value === '' || value === '0') {
          // When empty or zero, set quantity to 1 for calculations but keep input empty
          item.quantity = 1;
          item.line_total = 0; // Don't calculate when input is invalid
        } else {
          const numValue = parseInt(value);
          if (!isNaN(numValue) && numValue > 0) {
            item.quantity = numValue;
            item.line_total = item.quantity * item.unit_price;
          } else {
            // Invalid input, keep previous quantity but show the user's input
            item.line_total = 0;
          }
        }
      } else {
        const numValue = Math.max(1, Math.round(value));
        item.quantity = numValue;
        (item as any).quantityInput = numValue.toString();
        item.line_total = item.quantity * item.unit_price;
      }
    } else if (field === 'unit_price') {
      const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
      item[field] = Math.max(0, numValue);
      
      // Recalculate line total
      item.line_total = item.quantity * item.unit_price;
    } else {
      (item as Record<string, unknown>)[field] = value;
    }
    
    updatedItems[index] = item;
    setLocalItems(updatedItems);
    onChange(updatedItems);
  };

  const moveItem = (fromIndex: number, toIndex: number) => {
    const updatedItems = [...localItems];
    const [movedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(toIndex, 0, movedItem);
    
    // Update display order
    const reorderedItems = updatedItems.map((item, index) => ({
      ...item,
      display_order: index
    }));
    
    setLocalItems(reorderedItems);
    onChange(reorderedItems);
  };

  const calculateSubtotal = () => {
    return localItems.reduce((sum, item) => sum + item.line_total, 0);
  };

  const getQuoteLimit = () => {
    if (!quoteLimits) return null;
    
    switch (priceType) {
      case 'fixed':
        return quoteLimits.fixed_price;
      case 'estimate':
        return quoteLimits.estimated_price;
      case 'range':
        return quoteLimits.max_price;
      default:
        return null;
    }
  };


  const getUnitSuggestions = () => {
    // Use category-specific suggestions if available
    if (categoryUnitSuggestions && Array.isArray(categoryUnitSuggestions)) {
      return categoryUnitSuggestions
        .sort((a: any, b: any) => (a.order || 999) - (b.order || 999))
        .map((item: any) => item.label || item);
    }
    
    // Fallback to generic suggestions
    return [
      'item', 'hour', 'day', 'week', 'month', 'year',
      'sq ft', 'sq m', 'linear ft', 'linear m',
      'room', 'window', 'door', 'service'
    ];
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-800">Quote Breakdown</h4>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addItem}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Item
        </Button>
      </div>

      {localItems.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-6 text-center">
            <Calculator className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-3">
              No items added yet. Start building your quote breakdown.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addItem}
            >
              Add First Item
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {localItems.map((item, index) => {
            const isExpanded = expandedItemIndex === index;
            
            return (
              <Card key={index} className="border-gray-200">
                <CardContent className="p-4">
                  {/* Item Header - Always visible */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="hidden sm:flex flex-shrink-0">
                        <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {item.label || `Item ${index + 1}`}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.quantity} {item.unit_label} × {currency}{item.unit_price} = {currency}{item.line_total.toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedItemIndex(isExpanded ? null : index)}
                        className="h-8 w-8 p-0"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(index)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Item Form - Collapsible */}
                  {isExpanded && (
                    <div className="mt-4 space-y-3 border-t pt-4">
                    {/* Item Label */}
                    <div>
                      <Label className="text-xs text-gray-600">Item Description</Label>
                      <Input
                        placeholder="e.g. Interior painting, Cleaning service..."
                        value={item.label}
                        onChange={(e) => updateItem(index, 'label', e.target.value)}
                        className="mt-1"
                      />
                    </div>

                    {/* Quantity, Unit, and Price */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-gray-600">Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          placeholder="1"
                          value={(item as any).quantityInput ?? item.quantity?.toString() ?? ''}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          onBlur={(e) => {
                            // On blur, ensure we have a valid quantity
                            const value = e.target.value;
                            if (value === '' || parseInt(value) < 1) {
                              updateItem(index, 'quantity', '1');
                            }
                          }}
                          onWheel={(e) => e.currentTarget.blur()}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Unit</Label>
                        {customUnitItems.has(item.id || `index-${index}`) ? (
                          <div className="mt-1 space-y-2">
                            <Input
                              placeholder="Enter custom unit (e.g., packages, visits, etc.)"
                              value={item.unit_label}
                              onChange={(e) => updateItem(index, 'unit_label', e.target.value)}
                              className="text-sm"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newCustomUnits = new Set(customUnitItems);
                                newCustomUnits.delete(item.id || `index-${index}`);
                                setCustomUnitItems(newCustomUnits);
                                updateItem(index, 'unit_label', 'item'); // Reset to default
                              }}
                              className="text-xs h-6 px-2 text-muted-foreground hover:text-foreground"
                            >
                              Use suggested units instead
                            </Button>
                          </div>
                        ) : (
                          <Select
                            value={item.unit_label}
                            onValueChange={(value) => {
                              if (value === 'custom') {
                                const newCustomUnits = new Set(customUnitItems);
                                newCustomUnits.add(item.id || `index-${index}`);
                                setCustomUnitItems(newCustomUnits);
                                updateItem(index, 'unit_label', ''); // Clear for custom input
                              } else {
                                updateItem(index, 'unit_label', value);
                              }
                            }}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select unit" />
                            </SelectTrigger>
                            <SelectContent>
                              {getUnitSuggestions().map(unit => (
                                <SelectItem key={unit} value={unit}>
                                  {unit}
                                </SelectItem>
                              ))}
                              <SelectItem value="custom">
                                <span className="font-medium text-blue-600">Custom unit...</span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Unit Price ({currency})</Label>
                        <Input
                          type="number"
                          min="0.01"
                          step="0.01"
                          placeholder="0.01"
                          value={item.unit_price || ''}
                          onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                          onWheel={(e) => e.currentTarget.blur()}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Subtotal */}
          {localItems.length > 0 && (
            <Card className="border-gray-300 bg-gray-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-800">
                    Items Subtotal:
                  </span>
                  <div className="text-lg font-semibold text-gray-900">
                    {currency}{calculateSubtotal().toFixed(2)}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {localItems.length > 0 && (
        <div className="text-xs text-gray-600 bg-blue-50 p-3 rounded-lg">
          <strong>Tip:</strong> {priceType === 'fixed' 
            ? 'For fixed quotes, the total should match the sum of all line items.'
            : priceType === 'estimate'
            ? 'For estimates, this breakdown helps justify your estimated total.'
            : 'For ranges, this breakdown shows the minimum itemized costs. The range should account for variables.'
          }
        </div>
      )}
    </div>
  );
};